import React, { memo, Fragment, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import cn from 'classnames';
import * as PropTypes from 'prop-types';

import Button from '../../Button';
import TextInput from '../../TextInput';
import SelectCustom from '../../SelectCustom';
import RadioInput from '../../common/RadioInput';
import AsyncPaginatedSelect from '../../common/AsyncPaginatedSelect';

import { actions as primaryProviderListActions } from '../../../redux/appointmentForm/requests/primary-providers-list';
import usePCPWithExtraFieldsForm from './hooks/usePCPWithExtraFieldsForm';
import useAsyncPaginatedSelect from '../../../hooks/useAsyncPaginatedSelect';
import { getPcpNotListedOption } from '../../../utils/getPCPDictionaries';
import {
  selectOrganizationPrimaryCareProvidersList,
  selectOrganizationPrimaryCareProvidersListLoading,
  selectIsOrganizationPrimaryCareProvidersLastPage,
  selectFetchingOrganizationPrimaryCareProvidersListSucceed,
} from '../../../redux/appointmentForm/selectors';

import styles from './index.styl';
import { IDS } from '../../../constants/ids';

const RADIO_BUTTON_GROUP_NAME = 'isUserHasPCP';

const asyncPaginatedSelectConfig = {
  clearEntitiesAction: primaryProviderListActions.clear,
  entitiesIsLastPageSelector: selectIsOrganizationPrimaryCareProvidersLastPage,
  entitiesLoadingSelector: selectOrganizationPrimaryCareProvidersListLoading,
  entitiesSelector: selectOrganizationPrimaryCareProvidersList,
  entitiesFetchingSucceedSelector: selectFetchingOrganizationPrimaryCareProvidersListSucceed,
  sendAction: primaryProviderListActions.send,
};

const mapPcpListItemToOption = entity => ({
  label: `${entity.firstName} ${entity.lastName}`,
  value: entity.id,
});

const PrimaryCareProviderWithExtraFieldsForm = ({
  handleNext,
  initialValues,
  organizationId,
}) => {
  const { formatMessage } = useIntl();

  const {
    considerUsAsYourPCPOptions,
    formData,
    handleConsiderUsAsYourPCPChange,
    handleOptionChange,
    handlePcpNameChange,
    handlePcpChange,
    isNextButtonDisabled,
    optionsList,
    pcpNo,
    pcpYes,
    setPcpNameFieldRef,
    showPcpNameField,
  } = usePCPWithExtraFieldsForm(initialValues);

  const textfieldLabel = formatMessage({ id: 'fields.pcpName' });
  const selectLabel = formatMessage({ id: 'fields.doYouConsiderUsAsYourPCP' });
  const selectPcpNameLabel = formatMessage({
    id: 'fields.pcpName.selectPcpName',
  });
  const selectPcpNamePlaceholder = formatMessage({ id: 'common.startTyping' });

  const asyncSelectHookProps = useMemo(
    () => ({
      defaultOption: getPcpNotListedOption(formatMessage),
      ...asyncPaginatedSelectConfig,
      additionalRequestParams: { id: organizationId },
      mapToOptionsFn: mapPcpListItemToOption,
    }),
    [formatMessage, organizationId],
  );

  const {
    handleInputChange,
    entities,
    handleMenuScrollToBottom,
    isLoading,
    shouldPreselectDefaultOption,
  } = useAsyncPaginatedSelect(asyncSelectHookProps);

  const onSubmit = e => {
    e.preventDefault();
    handleNext(formData);
  };

  useEffect(() => {
    if (shouldPreselectDefaultOption && pcpYes) {
      handlePcpChange(asyncSelectHookProps.defaultOption);
    }
  }, [shouldPreselectDefaultOption, pcpYes]);

  return (
    <form
      onSubmit={onSubmit}
      className={styles.root}
      id={IDS.PCP_QUESTIONS_WITH_EXTRA_FIELDS_FORM}
    >
      <div className={styles.radioButtonItem}>
        <RadioInput
          label={optionsList[0].label}
          value={optionsList[0].value}
          name={RADIO_BUTTON_GROUP_NAME}
          checked={pcpYes}
          onChange={handleOptionChange(optionsList[0].value)}
        />
      </div>

      {pcpYes && (
        <Fragment>
          <div className={styles.textField}>
            <AsyncPaginatedSelect
              handleInputChange={handleInputChange}
              isLoading={isLoading}
              label={selectPcpNameLabel}
              onChange={handlePcpChange}
              onMenuScrollToBottom={handleMenuScrollToBottom}
              options={entities}
              placeholder={selectPcpNamePlaceholder}
              value={formData.pcp}
            />
          </div>

          <div
            className={cn(styles.textField, {
              [styles.hideTextField]: !showPcpNameField,
            })}
          >
            <TextInput
              label={textfieldLabel}
              onChange={handlePcpNameChange}
              placeholder={textfieldLabel}
              setRef={setPcpNameFieldRef}
              type="text"
              value={formData.pcpName}
            />
          </div>
        </Fragment>
      )}

      <div className={styles.radioButtonItem}>
        <RadioInput
          label={optionsList[1].label}
          value={optionsList[1].value}
          name={RADIO_BUTTON_GROUP_NAME}
          checked={pcpNo}
          onChange={handleOptionChange(optionsList[1].value)}
        />
      </div>

      {pcpNo && (
        <div className={styles.selectField}>
          <SelectCustom
            label={selectLabel}
            placeholder={selectLabel}
            options={considerUsAsYourPCPOptions}
            onChange={handleConsiderUsAsYourPCPChange}
            value={formData.considerUsPcp}
          />
        </div>
      )}

      <div className={styles.radioButtonItem}>
        <RadioInput
          label={optionsList[2].label}
          value={optionsList[2].value}
          name={RADIO_BUTTON_GROUP_NAME}
          checked={formData.pcpProviderAnswer === optionsList[2].value}
          onChange={handleOptionChange(optionsList[2].value)}
        />
      </div>

      <div className="mt4 tr">
        <Button
          type="submit"
          raised
          color="primary"
          disabled={isNextButtonDisabled}
        >
          {formatMessage({ id: 'common.next' })}
        </Button>
      </div>
    </form>
  );
};

PrimaryCareProviderWithExtraFieldsForm.propTypes = {
  handleNext: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  organizationId: PropTypes.number,
};

export default memo(PrimaryCareProviderWithExtraFieldsForm);



// WEBPACK FOOTER //
// ./src/components/forms/PrimaryCareProviderWithExtraFieldsForm/index.jsx